/**
 * VoteMate Design System Theme
 * Combines all design tokens into a Material-UI theme
 */

import { createTheme } from '@mui/material/styles';
import {
  primitiveColors,
  semanticColors,
  typographyTokens,
  spacingTokens,
  BORDER_RADIUS,
} from './tokens';

// Create the theme with our design tokens. Accepts an optional direction so
// DirectionProvider can produce an RTL-aware theme without duplicating options.
export function createAppTheme(direction: 'ltr' | 'rtl' = 'ltr') {
  const t = createTheme({
    direction,
    palette: {
      mode: 'light',
      primary: {
        main: semanticColors.primary,
        contrastText: semanticColors.textOnPrimary,
      },
      secondary: {
        main: semanticColors.accent,
        contrastText: semanticColors.textOnPrimary,
      },
      background: {
        default: semanticColors.background,
        paper: semanticColors.surface,
      },
      text: {
        primary: semanticColors.textPrimary,
        secondary: semanticColors.textSecondary,
        disabled: semanticColors.textDisabled,
      },
      divider: semanticColors.border,
      grey: {
        50: primitiveColors.gray1,
        100: primitiveColors.gray2,
        200: primitiveColors.gray3,
        300: primitiveColors.gray4,
        400: primitiveColors.gray7,
        500: primitiveColors.gray8,
        600: primitiveColors.gray10,
        700: primitiveColors.gray11,
        800: primitiveColors.gray12,
        900: primitiveColors.black,
      },
    },
    typography: {
      fontFamily: typographyTokens.fontFamily,
      h1: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: typographyTokens.heading1.fontSize,
        fontWeight: typographyTokens.heading1.fontWeight,
        lineHeight: typographyTokens.heading1.lineHeight,
      },
      h2: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: typographyTokens.heading2.fontSize,
        fontWeight: typographyTokens.heading2.fontWeight,
        lineHeight: typographyTokens.heading2.lineHeight,
      },
      h3: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: typographyTokens.heading3.fontSize,
        fontWeight: typographyTokens.heading3.fontWeight,
        lineHeight: typographyTokens.heading3.lineHeight,
      },
      h4: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: typographyTokens.heading4.fontSize,
        fontWeight: typographyTokens.heading4.fontWeight,
        lineHeight: typographyTokens.heading4.lineHeight,
      },
      h5: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: typographyTokens.heading5.fontSize,
        fontWeight: typographyTokens.heading5.fontWeight,
        lineHeight: typographyTokens.heading5.lineHeight,
      },
      h6: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: typographyTokens.heading6.fontSize,
        fontWeight: typographyTokens.heading6.fontWeight,
        lineHeight: typographyTokens.heading6.lineHeight,
      },
      body1: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: typographyTokens.body1.fontSize,
        fontWeight: typographyTokens.body1.fontWeight,
        lineHeight: typographyTokens.body1.lineHeight,
      },
      body2: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: typographyTokens.body2.fontSize,
        fontWeight: typographyTokens.body2.fontWeight,
        lineHeight: typographyTokens.body2.lineHeight,
      },
      button: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: typographyTokens.button.fontSize,
        fontWeight: typographyTokens.button.fontWeight,
        textTransform: typographyTokens.button.textTransform,
      },
      caption: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: typographyTokens.caption.fontSize,
        fontWeight: typographyTokens.caption.fontWeight,
        lineHeight: typographyTokens.caption.lineHeight,
      },
      overline: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: typographyTokens.overline.fontSize,
        fontWeight: typographyTokens.overline.fontWeight,
        lineHeight: typographyTokens.overline.lineHeight,
        textTransform: typographyTokens.overline.textTransform,
      },
      // Additional Material-UI typography variants
      subtitle1: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: 16,
        fontWeight: 400,
        lineHeight: 1.75,
      },
      subtitle2: {
        fontFamily: typographyTokens.fontFamily,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.57,
      },
    },
    spacing: (factor: number) => spacingTokens.md * factor,
    components: {
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: typographyTokens.fontFamily,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: typographyTokens.fontFamily,
            borderRadius: BORDER_RADIUS,
            padding: `${spacingTokens.buttonPadding}px ${spacingTokens.lg}px`,
            textTransform: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            padding: spacingTokens.cardPadding,
            borderRadius: BORDER_RADIUS,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: typographyTokens.fontFamily,
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontFamily: typographyTokens.fontFamily,
          },
        },
      },
    },
  });
  t.tokens = { colors: semanticColors, spacing: spacingTokens, typography: typographyTokens };
  return t;
}

// Augment the theme with our custom tokens for direct access
declare module '@mui/material/styles' {
  interface Theme {
    tokens: {
      colors: typeof semanticColors;
      spacing: typeof spacingTokens;
      typography: typeof typographyTokens;
    };
  }

  interface ThemeOptions {
    tokens?: {
      colors?: typeof semanticColors;
      spacing?: typeof spacingTokens;
      typography?: typeof typographyTokens;
    };
  }
}

export const theme = createAppTheme();

export default theme;
